import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatestWith, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';

import {
  CalibrationService,
  CanvasActionsService,
  CanvasCommunicationService,
  DevicesService,
  FeatureFlagsConfigService,
  IndicationsTableService,
  LayoutService,
  RemoteControlService,
} from '@olympus/ngx-shared';

import { WindViewState } from '../../app.state';
import { IndicationService } from '../../services/indication/indication.service';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: false,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  @Output() public readonly quickActionsToggled: EventEmitter<{ opened: boolean, width: number }> =
    new EventEmitter<{ opened: boolean, width: number }>();

  @Input() public isFileLoaded = false;

  @ViewChild('quickActions', { read: ElementRef }) public quickActionsElementRef: ElementRef | undefined;

  private readonly _unsubscriber$: Subject<void> = new Subject();

  public isNotAnalysisMode$: Observable<boolean> = this._canvasActionsService.isAnalysisMode$.pipe(map(mode => !mode));
  public isInspectionMode$: Observable<boolean> = this._canvasActionsService.isInspectionMode$.pipe(
    tap((isInspection) => {
      this.isPlaying = isInspection;
    }),
  );

  public isNotSetupMode$: Observable<boolean> = this._canvasActionsService.isSetupMode$.pipe(map(mode => !mode));

  public isInspectionInProgress$: Observable<boolean> = this.isInspectionMode$.pipe(
    combineLatestWith(this._canvasActionsService.isPauseMode$),
    map(([isInspectionMode, isPauseMode]) => isInspectionMode || isPauseMode),
  );

  public isRemotelyControlled = false;
  public isPlaying = false;
  public disableAddIndication = true;
  public isNdeFile = false;
  public featureFlagAnalysis: boolean;
  public hasDeviceDisconnectedOrError = false;
  public isSingleBeam = true;

  private _reloadViewsTimeout: any;

  public get haveInspection(): boolean {
    return !!this._featureFlagsConfigService.getFeature('inspection');
  }

  constructor(
    private readonly _devicesService: DevicesService,
    private readonly _state$: WindViewState,
    private readonly _router: Router,
    private readonly _canvasActionsService: CanvasActionsService,
    private readonly _remoteControlService: RemoteControlService,
    private readonly _layoutService: LayoutService,
    private readonly _calibrationService: CalibrationService,
    private readonly _indicationService: IndicationService,
    private readonly _indicationsTableService: IndicationsTableService,
    private readonly _communicationService: CanvasCommunicationService,
    private readonly _featureFlagsConfigService: FeatureFlagsConfigService,
    private readonly _changeDetectorRef: ChangeDetectorRef,

  ) {
    this.featureFlagAnalysis = this._featureFlagsConfigService.getFeature('analysis');
  }

  public ngOnInit(): void {
    this._state$.get('FileExtension')
      .pipe(
        map(fileExtension => fileExtension.value === '.nde'),
        takeUntil(this._unsubscriber$),
      )
      .subscribe(isNde => this.isNdeFile = isNde);

    this._remoteControlService.isRemotelyControlled$.pipe(
      takeUntil(this._unsubscriber$),
    ).subscribe((controlled) => {
      this.isRemotelyControlled = controlled === true;
    });

    this._communicationService.context.activeGroupId$
      .pipe(
        switchMap(() => this._communicationService.context.activeGroupModel.utSettingsModel.beamsModel.beamsLength$),
        takeUntil(this._unsubscriber$),
      ).subscribe((length: number) => {
        this.isSingleBeam = length === 1;
      });

    this._communicationService.context.activeGroupId$
      .pipe(
        switchMap(() => this._communicationService.context.activeGroupModel.indicationZonesModel.canAddIndication),
        combineLatestWith(this.isNotAnalysisMode$),
        takeUntil(this._unsubscriber$),
      ).subscribe(([canAddIndication, isNotAnalysisMode]) => {
        this.disableAddIndication = !canAddIndication || isNotAnalysisMode;
      });

    this._devicesService.hasDeviceDisconnectedOrError$.pipe(
      takeUntil(this._unsubscriber$))
      .subscribe((hasDeviceDisconnectedOrError) => {
        this.hasDeviceDisconnectedOrError = hasDeviceDisconnectedOrError;
      },
      );
  }

  public togglePlayPause(): void {
    if (this.isPlaying) {
      this._canvasActionsService.pauseInspection();
    } else {
      this._canvasActionsService.startInspection();
    }
  }

  public stop(): void {
    this._canvasActionsService.stopInspection();
  }

  public goToSetup(): void {
    this._canvasActionsService.goToSetup();
  }

  public openCalibration(): void {
    if (!this.isSingleBeam) {
      this._calibrationService.open();
    }
  }

  public quickAddIndication(): void {
    this._indicationsTableService.addIndication(this._indicationService.indicationReadings);
  }

  public toggleIndicationsTable(): void {
    const outlet: string | null = this._router.url.includes('indications-table')
      ? null
      : 'table/indications-table/inspection';

    this._router.navigate(['/', { outlets: { table: outlet } }]);

    this._reloadViewsTimeout = setTimeout(() => {
      this._layoutService.reloadViews();
    }, 100); // just enough time for table to render, so new height is being taken
  }

  public onQuickActionsToggled(args: { opened: boolean }): void {
    this._changeDetectorRef.detectChanges();
    this.quickActionsToggled.emit({ opened: args.opened, width: this.quickActionsElementRef?.nativeElement.offsetWidth });
  }

  public ngOnDestroy(): void {
    this._unsubscriber$.next();
    clearTimeout(this._reloadViewsTimeout);
  }
}
